import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { BrandGroupSelect, usePermanentLocations, usePermanentRealtimeDealers, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { ROLES } from "models";
import { usePasswordStrengthMutation } from "modules/AccountSettings/components/ChangePasswordModal/hooks";
import { PASSWORD_STRENGTH_THRESHOLD } from "modules/Login/components";
import { PasswordCheckmarkIcon } from "modules/UsersDashboard/components";
import { useAssignedAppointmentsCountQuery } from "modules/UsersDashboard/components/EditUserModal/hooks";
import { useMechanicsOptions } from "modules/UsersDashboard/components/UserForm/hooks";
import "modules/UsersDashboard/components/UserForm/UserForm.scss";
import { getAllowedLocations, useGetDmsUsersOptions, useGetUserRoleOptions } from "modules/UsersDashboard/hooks";

import type { DropdownItemProps } from "components";
import type { BrandGroupID, User } from "models";
import type { DropdownItemProps as SemanticDropdownItemProps } from "semantic-ui-react";

type DealerLocationOption = {
  key: string | number;
  value: string | number;
  text: string;
  disabled?: boolean;
  content?: string;
};

type UserFormProps = {
  hasErrors: boolean;
  userId?: number | null;
  isEdit?: boolean;
  canEditAll: boolean;
  canEditDealerAdminFields: boolean;
  passwordStrength: number;
  onPasswordStrengthChange: (strength: number) => void;
};

export type CreateEditUserForm = Pick<
  User,
  | "username"
  | "email"
  | "first_name"
  | "last_name"
  | "dms_nr"
  | "dms_location_source"
  | "external_user_id"
  | "multi_device_login_enabled"
  | "csv_download"
  | "is_keylocker_allowed"
  | "is_counter_tablet_user"
  | "tyre_ordering_enabled"
  | "is_combi_user"
  | "has_leased_tablet"
> & {
  dealers_ids: number[] | null;
  location_ids: number[] | null;
  role_id: number | null; // TODO: why is this field nullable ? it's already present on the user so it should be picked from there ? and not nullable as a role is mandatory ? likely the same for other fields here
  brand_group_ids: BrandGroupID[];
  password: string;
  confirmPassword: string;
  dealer_location_id: number | null;
  phone: string | null;
  isActive: boolean;
  onei_planning_monteur_id: number | null;
  planit_planning_mechanic_id: number | null;
  dealer_id: number | null;
  is_tablet_translation_ai_enabled?: boolean;
};

const FormHelper = createFormHelper<CreateEditUserForm>();

const EXTRA_DEALERS_ROLES = [ROLES.Consultant, ROLES.Supervisor, ROLES.WarrantyManager, ROLES.Manufacturer];

const searchDealerLocations = (options: SemanticDropdownItemProps[], query: string) => {
  const search = query.toLowerCase();
  const filteredOptions: SemanticDropdownItemProps[] = [];
  let currentDealerHeader: SemanticDropdownItemProps | null = null;

  options.forEach(option => {
    if (option.disabled) {
      currentDealerHeader = option;
      return;
    }

    if (typeof option.text === "string" && option.text.toLowerCase().includes(search)) {
      if (currentDealerHeader) {
        filteredOptions.push(currentDealerHeader);
        currentDealerHeader = null;
      }
      filteredOptions.push(option);
    }
  });

  return filteredOptions;
};

export const UserForm = ({ isEdit, hasErrors, userId, canEditAll, canEditDealerAdminFields, onPasswordStrengthChange, passwordStrength }: UserFormProps) => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { selectedLocation } = useSelectedLocation();
  const { userRolesOptions } = useGetUserRoleOptions();
  const { watch, setValue } = FormHelper.useFormContext();
  const { checkPasswordStrength, strength, resetPasswordStrength } = usePasswordStrengthMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dealersWithLocationsOptions = dealers
    .filter(d => d.active)
    .sort((a, b) => a.name.localeCompare(b.name))
    .reduce((acc: DealerLocationOption[], dealer) => {
      acc.push({ key: `dealer-${dealer.id}`, value: dealer.id, text: dealer.name, disabled: true });

      if (dealer.locations && dealer.locations.length > 0) {
        const dealerLocations: DealerLocationOption[] = [];

        dealer.locations.forEach(location =>
          dealerLocations.push({ key: location.id, value: location.id, text: `${dealer.name} ${location.name}`, content: location.name })
        );

        acc.push(...dealerLocations.sort((a, b) => a.text.localeCompare(b.text)));
      }

      return acc;
    }, []);

  const [email, role, password, username, last_name, first_name, dealerIds, dealerLocationId, confirmPassword, formDealerId, formAllowedLocationsIds] = watch([
    "email",
    "role_id",
    "password",
    "username",
    "last_name",
    "first_name",
    "dealers_ids",
    "dealer_location_id",
    "confirmPassword",
    "dealer_id",
    "location_ids"
  ]);

  const canEditDealerAdmin = canEditAll || canEditDealerAdminFields;
  const formSelectedLocation = locations.find(element => element.id === dealerLocationId);
  const formSelectedDealer = dealers.find(element => element.id === formSelectedLocation?.dealer_id);
  const customSelectedValue = formSelectedDealer && formSelectedLocation ? `${formSelectedDealer.name} - ${formSelectedLocation.name}` : undefined;

  const showTabletAiCheckbox = isEdit && role === ROLES.Mechanic && formSelectedLocation?.is_tablet_translation_ai_enabled;
  const extraDealersOptions = dealers
    .filter(d => d.id !== formDealerId)
    .map(dealer => ({ key: dealer.id, value: dealer.id, text: dealer.name }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const warrantyRoleDealersOptions = dealers.map(({ id, name }) => ({ key: id, value: id, text: name })).sort((a, b) => a.text.localeCompare(b.text));
  const warrantyRoleLocationsOptions =
    formAllowedLocationsIds?.reduce<DropdownItemProps[]>((acc, locId) => {
      const location = locations.find(loc => loc.id === locId);
      if (location) acc.push({ key: location.id, value: location.id, text: location.name });

      return acc;
    }, []) || [];

  const { oneIPlanningMechanicsOptions, plainItMechanicsOptions } = useMechanicsOptions();
  const { dmsUserOptions, isDmsUsersOptionsLoading } = useGetDmsUsersOptions(dealerLocationId ?? 0, userId);
  const { warrantyRoleAllowedLocationsOptions } = getAllowedLocations({ dealers, dealerIds: formDealerId ? [formDealerId, ...(dealerIds ?? [])] : (dealerIds ?? []) });

  const { data: assignedAppointmentCount } = useAssignedAppointmentsCountQuery({
    is_dayplanner_enabled: selectedLocation?.is_dayplanner_enabled,
    user_id: userId,
    dealer_location_id: dealerLocationId,
    isMechanicRole: Boolean(role && [ROLES.ChiefMechanic, ROLES.Mechanic].includes(role))
  });

  const handleDealerLocationChange = (value?: number | null) => {
    if (isEdit && assignedAppointmentCount && assignedAppointmentCount > 0)
      return toast.error(t("v8_can_not_change_location", "You can not change location as long as this user has work orders assigned."));

    if (!value) return;

    setValue("dealer_location_id", value);

    const matchedDealer = dealers.find(d => d.locations?.some(l => l.id === value));
    if (matchedDealer) setValue("dealer_id", matchedDealer.id);
  };

  const handleRoleChange = (value?: ROLES | null) => {
    if (!value) return;

    setValue("role_id", value);

    if (value !== ROLES.WarrantyManager) setValue("location_ids", null);
    if (!EXTRA_DEALERS_ROLES.includes(value)) setValue("dealers_ids", null);

    if (value === ROLES.WarrantyManager && dealerLocationId && !formAllowedLocationsIds?.includes(dealerLocationId)) setValue("dealer_location_id", null);
  };

  useEffect(() => {
    if (!password) {
      resetPasswordStrength();
      return;
    }

    checkPasswordStrength({ password, email, username, first_name, last_name });
  }, [password, email, first_name, last_name, username]);

  useEffect(() => {
    onPasswordStrengthChange(strength ?? 0);
  }, [strength]);

  return (
    <div className="UserForm">
      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!role && hasErrors}>
              <label>{t("v8_role", "Role")} </label>
              <FormHelper.Field.Dropdown
                name="role_id"
                options={userRolesOptions}
                onChange={data => handleRoleChange(data)}
                dropdownProps={{
                  placeholder: t("v8_role", "Role"),
                  fluid: true,
                  disabled: !canEditAll
                }}
              />
            </Form.Field>
          </GridColumn>

          {role === ROLES.WarrantyManager ? (
            <>
              <GridColumn>
                <Form.Field required error={!formDealerId && hasErrors}>
                  <label>{t("v8_default_dealer", "Default Dealer")} </label>
                  <FormHelper.Field.Dropdown
                    name="dealer_id"
                    options={warrantyRoleDealersOptions}
                    dropdownProps={{
                      placeholder: t("v8_default_dealer", "Default Dealer"),
                      fluid: true,
                      search: true,
                      selectOnBlur: false,
                      clearable: true,
                      disabled: !canEditAll
                    }}
                  />
                </Form.Field>
              </GridColumn>

              <GridColumn>
                <Form.Field required error={!dealerLocationId && hasErrors}>
                  <label>{t("v8_default_location", "Default Location")} </label>
                  <FormHelper.Field.Dropdown
                    name="dealer_location_id"
                    options={warrantyRoleLocationsOptions}
                    dropdownProps={{
                      placeholder: t("v8_default_location", "Default Location"),
                      fluid: true,
                      search: true,
                      selectOnBlur: false,
                      clearable: true,
                      disabled: !canEditAll,
                      noResultsMessage: t("v8_assign_allowed_locations_first", "Assign allowed locations first")
                    }}
                  />
                </Form.Field>
              </GridColumn>
            </>
          ) : (
            <GridColumn>
              <Form.Field required error={!dealerLocationId && hasErrors}>
                <label>{t("v8_default_dealer_location", "Default Dealer / Location")} </label>
                <FormHelper.Field.Dropdown
                  name="dealer_location_id"
                  options={dealersWithLocationsOptions}
                  onChange={data => handleDealerLocationChange(data)}
                  customSelectedValue={customSelectedValue}
                  dropdownProps={{
                    placeholder: t("v8_default_dealer_location", "Default Dealer / Location"),
                    fluid: true,
                    search: searchDealerLocations,
                    disabled: !canEditAll
                  }}
                />
              </Form.Field>
            </GridColumn>
          )}

          {!(role === ROLES.WarrantyManager) && (
            <GridColumn>
              <Form.Field>
                <label>{t("v8_brand_groups", "Brand Groups")} </label>
                <FormHelper.Controller
                  name="brand_group_ids"
                  render={({ field }) => <BrandGroupSelect showAllBrands value={field.value} onChange={field.onChange} disabled={!canEditAll} />}
                />
              </Form.Field>
            </GridColumn>
          )}
        </Grid>

        {role && EXTRA_DEALERS_ROLES.includes(role) && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field>
                <label>{t("v8_extra_dealers", "Extra Dealers")} </label>
                <FormHelper.Field.Dropdown
                  name="dealers_ids"
                  options={extraDealersOptions}
                  dropdownProps={{
                    placeholder: t("v8_extra_dealers", "Extra Dealers"),
                    fluid: true,
                    multiple: true,
                    search: true,
                    selection: true,
                    selectOnBlur: false,
                    clearable: true,
                    disabled: !canEditAll
                  }}
                />
              </Form.Field>
            </GridColumn>

            {role === ROLES.WarrantyManager ? (
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_allowed_locations", "Allowed locations")} </label>
                  <FormHelper.Field.Dropdown
                    name="location_ids"
                    options={warrantyRoleAllowedLocationsOptions}
                    dropdownProps={{
                      placeholder: t("v8_allowed_locations", "Allowed locations"),
                      fluid: true,
                      multiple: true,
                      search: true,
                      selection: true,
                      selectOnBlur: false,
                      clearable: true,
                      disabled: !canEditAll
                    }}
                  />
                </Form.Field>
              </GridColumn>
            ) : (
              <GridColumn />
            )}

            {role === ROLES.WarrantyManager ? (
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_brand_groups", "Brand Groups")} </label>
                  <FormHelper.Controller
                    name="brand_group_ids"
                    render={({ field }) => <BrandGroupSelect showAllBrands value={field.value} onChange={field.onChange} disabled={!canEditAll} />}
                  />
                </Form.Field>
              </GridColumn>
            ) : (
              <GridColumn />
            )}
          </Grid>
        )}

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!username && hasErrors}>
              <label>{t("v8_username_email", "Username / Email")} </label>
              <FormHelper.Field.Input
                name="username"
                inputProps={{
                  placeholder: t("v8_username_email", "Username / Email"),
                  fluid: true,
                  type: "text",
                  autoComplete: "new-password",
                  disabled: !canEditAll
                }}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field
              required
              error={hasErrors && ((!isEdit && !password) || (!!password && (password !== confirmPassword || passwordStrength < PASSWORD_STRENGTH_THRESHOLD)))}
            >
              <label>{t("v8_new_password", "New Password")}</label>
              <div className="password-outer-container">
                <div className="password-inner-container">
                  <FormHelper.Field.Input
                    name="password"
                    inputProps={{
                      placeholder: t("v8_new_password", "New Password"),
                      fluid: true,
                      type: showPassword ? "text" : "password",
                      autoComplete: "new-password",
                      disabled: !canEditDealerAdmin,
                      icon: (
                        <div className="password-show-icon-container">
                          <Icon className={`password-icon ${showPassword ? "icon eye slash" : "icon eye"}`} onClick={() => setShowPassword(!showPassword)} />
                        </div>
                      )
                    }}
                  />
                </div>
                {password.length > 0 && (
                  <div className="password-checkmark-icon-container">
                    <PasswordCheckmarkIcon password={password} type="new" passwordStrength={passwordStrength} />
                  </div>
                )}
              </div>
              {password.length > 0 && (
                <div className="strength-meter">
                  <div className="strength-meter-fill" data-strength={passwordStrength} />
                </div>
              )}
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={hasErrors && !!password && password !== confirmPassword}>
              <label>{t("v8_confirm_new_password", "Confirm New Password")} </label>
              <div className="password-outer-container">
                <div className="password-inner-container">
                  <FormHelper.Field.Input
                    name="confirmPassword"
                    inputProps={{
                      placeholder: t("v8_confirm_new_password", "Confirm New Password"),
                      fluid: true,
                      type: showConfirmPassword ? "text" : "password",
                      autoComplete: "new-password",
                      disabled: !canEditDealerAdmin,
                      icon: (
                        <div className="password-show-icon-container">
                          <Icon
                            className={`password-icon ${showConfirmPassword ? "icon eye slash" : "icon eye"}`}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          />
                        </div>
                      )
                    }}
                  />
                </div>
                {confirmPassword.length > 0 && (
                  <div className="password-checkmark-icon-container">
                    <PasswordCheckmarkIcon confirmPassword={confirmPassword} password={password} type="confirm" passwordStrength={passwordStrength} />
                  </div>
                )}
              </div>
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!email && hasErrors}>
              <label>{t("v8_sso_email", "SSO Email")} </label>
              <FormHelper.Field.Input name="email" inputProps={{ placeholder: t("v8_sso_email", "SSO Email"), fluid: true, disabled: !canEditAll }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={!first_name && hasErrors}>
              <label>{t("v8_first_name", "First Name")}</label>
              <FormHelper.Field.Input name="first_name" inputProps={{ placeholder: t("v8_first_name", "First Name"), fluid: true, disabled: !canEditAll }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={!last_name && hasErrors}>
              <label>{t("v8_last_name", "Last Name")} </label>
              <FormHelper.Field.Input name="last_name" inputProps={{ placeholder: t("v8_last_name", "Last Name"), fluid: true, disabled: !canEditAll }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field>
              <label>{t("v8_phone", "Phone")} </label>
              <FormHelper.Field.Input name="phone" inputProps={{ placeholder: t("v8_phone", "Phone"), fluid: true, disabled: !canEditDealerAdmin }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_dms_user", "DMS User")}</label>
              <FormHelper.Field.Dropdown
                name="dms_nr"
                options={dmsUserOptions}
                dropdownProps={{
                  placeholder: t("v8_dms_user", "DMS User"),
                  fluid: true,
                  search: true,
                  selection: true,
                  selectOnBlur: false,
                  clearable: true,
                  loading: isDmsUsersOptionsLoading,
                  disabled: !canEditAll
                }}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_dms_location_source", "DMS Location Source")}</label>
              <FormHelper.Field.Input
                name="dms_location_source"
                inputProps={{ placeholder: t("v8_dms_location_source", "DMS Location Source"), fluid: true, disabled: !canEditDealerAdmin }}
              />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_external_user_id", "External User ID")}</label>
              <FormHelper.Field.Input
                name="external_user_id"
                inputProps={{ placeholder: t("v8_external_user_id", "External User ID"), fluid: true, disabled: !canEditDealerAdmin }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        {(formSelectedDealer?.onei_planning_enabled || formSelectedDealer?.planit_planning_enabled) && role && [ROLES.Mechanic, ROLES.ChiefMechanic].includes(role) && (
          <Grid columns="equal">
            {formSelectedDealer.onei_planning_enabled && (
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_onei_planning_monteur", "1i Planning Monteur")} </label>
                  <FormHelper.Field.Dropdown
                    name="onei_planning_monteur_id"
                    options={oneIPlanningMechanicsOptions}
                    dropdownProps={{
                      placeholder: t("v8_onei_planning_monteur", "1i Planning Monteur"),
                      fluid: true,
                      selection: true,
                      selectOnBlur: false,
                      clearable: true,
                      disabled: !canEditAll
                    }}
                  />
                </Form.Field>
              </GridColumn>
            )}

            {formSelectedDealer.planit_planning_enabled && (
              <GridColumn>
                <Form.Field>
                  <label>{t("v8_planit_mechanic", "Plan IT Mechanic")}</label>
                  <FormHelper.Field.Dropdown
                    name="planit_planning_mechanic_id"
                    options={plainItMechanicsOptions}
                    dropdownProps={{
                      placeholder: t("v8_planit_mechanic", "Plan IT Mechanic"),
                      fluid: true,
                      search: true,
                      selection: true,
                      selectOnBlur: false,
                      clearable: true,
                      disabled: !canEditAll
                    }}
                  />
                </Form.Field>
              </GridColumn>
            )}
          </Grid>
        )}

        <div className={`checkboxes-container ${showTabletAiCheckbox ? "split-container" : ""}`}>
          <FormHelper.Field.Checkbox name="multi_device_login_enabled" label={t("v8_multidevice_login", "Multidevice login")} disabled={!canEditAll} />
          <FormHelper.Field.Checkbox name="csv_download" label={t("v8_can_download_csv", "Can download CSV file")} disabled={!canEditAll} />
          <FormHelper.Field.Checkbox name="is_keylocker_allowed" label={t("v8_keylocker_allowed", "Keylocker allowed")} disabled={!canEditAll} />
          <FormHelper.Field.Checkbox name="is_counter_tablet_user" label={t("v8_use_counter_tablets", "Use counter tablets")} disabled={!canEditAll} />
          {showTabletAiCheckbox && (
            <FormHelper.Field.Checkbox
              name="is_tablet_translation_ai_enabled"
              label={t("v8_translation_ai_on_tablet", "Translation AI on tablet")}
              disabled={!canEditAll}
            />
          )}
          <FormHelper.Field.Checkbox name="tyre_ordering_enabled" label={t("v8_enable_tyre_ordering", "Enable tyre ordering")} disabled={!canEditAll} />
        </div>
      </Modal.Content>
    </div>
  );
};
